// @version 3.0
// @link https://piccalil.li/tutorial/build-a-fully-responsive-progressively-enhanced-burger-menu/

class ToggleMenu extends HTMLElement {
	constructor() {
		super();

		const self = this;

		this.state = new Proxy(
			{
				status: "open",
				active: false,
			},
			{
				set(state, key, value) {
					const oldValue = state[key];

					state[key] = value;
					if (oldValue !== value) {
						self.processStateChange();
					}
					return state;
				},
			}
		);
	}

	get maxWidth() {
		return parseInt(this.getAttribute("max-width") || 99999, 10);
	}

	getFocusableElements(parent) {
		if (!parent) {
			console.warn("You need to pass a parent HTMLElement");
			return [];
		}

		return parent.querySelectorAll(
			'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled]), details:not([disabled]), summary:not(:disabled)'
		);
	}

	connectedCallback() {
		this.trigger = this.querySelector(".toggle-menu-trigger");
		this.triggerClasses = this.trigger.classList;
		this.triggerMarkup = this.trigger.innerHTML;

		this.panel = this.querySelector(".toggle-menu-panel");
		this.panelClasses = this.panel.classList;
		this.panelMarkup = this.panel.innerHTML;

		this.bar = this.querySelector(".toggle-menu-bar");
		this.barClasses = this.bar.classList;
		this.barMarkup = this.bar.innerHTML;

		this.render();

		const observer = new ResizeObserver((observedItems) => {
			const { contentRect } = observedItems[0];
			this.state.active = contentRect.width <= this.maxWidth;
		});

		if (this.hasAttribute("max-width")) {
			// We want to watch the parent like a hawk
			observer.observe(document.documentElement);
		}
	}

	render() {
		this.innerHTML = `
			<div class="${this.barClasses}">
				${this.barMarkup}
				<button class="${this.triggerClasses}" type="button" aria-controls="toggle-menu-panel" id="toggle-menu-trigger">
					${this.triggerMarkup} 
				</button>
			</div>
			<div class="${this.panelClasses}" data-toggled-by="toggle-menu-trigger" id="toggle-menu-panel">
				${this.panelMarkup}
			</div>
		`;

		this.postRender();
	}

	postRender() {
		this.trigger = this.querySelector(".toggle-menu-trigger");
		this.panel = this.querySelector(".toggle-menu-panel");
		this.label = this.querySelector(".toggle-menu-label");

		if (this.trigger && this.panel) {
			this.classList.add("toggle-menu");

			this.toggle();

			this.trigger.addEventListener("click", (event) => {
				event.preventDefault();

				this.toggle();
			});

			window.addEventListener("hashchange", (event) => {
				this.toggle("closed");
			});

			document.addEventListener("keydown", (event) => {
				if (this.state.status === "closed") {
					return;
				}

				if (event.key === "Escape") {
					this.toggle("closed");
				}
			});

			document.addEventListener("focusin", () => {
				if (!this.contains(document.activeElement)) {
					this.toggle("closed");
				}
			});

			return;
		}

		this.innerHTML = this.menuMarkup;
	}

	setLabelText() {
		const labelOpen = this.getAttribute("label-open") ? this.getAttribute("label-open") : "Close menu";
		const labelClosed = this.getAttribute("label-closed") ? this.getAttribute("label-closed") : "Open menu";

		// console.log(this.state.status);

		switch (this.state.status) {
			case "closed":
				this.label.textContent = labelClosed;
				this.trigger.setAttribute("title", labelClosed);
				this.trigger.setAttribute("aria-label", labelClosed);
				break;
			case "open":
				this.label.textContent = labelOpen;
				this.trigger.setAttribute("title", labelOpen);
				this.trigger.setAttribute("aria-label", labelOpen);
				break;
		}
	}

	setAriaExpanded() {
		switch (this.state.status) {
			case "closed":
				this.removeAttribute("open");
				this.trigger.setAttribute("aria-expanded", "false");
				this.panel.setAttribute("aria-hidden", "true");
				break;
			case "open":
				this.setAttribute("open", "");
				this.trigger.setAttribute("aria-expanded", "true");
				this.panel.setAttribute("aria-hidden", "false");
				break;
		}

		// If the menu isn't active, set aria-hidden="false"
		if (!this.state.active) {
			this.panel.setAttribute("aria-hidden", "false");
		}
	}

	toggle(forcedStatus) {
		if (forcedStatus) {
			if (this.state.status === forcedStatus) {
				return;
			}

			this.state.status = forcedStatus;
		} else {
			this.state.status = this.state.status === "closed" ? "open" : "closed";
		}
	}

	processStateChange() {
		if (!this.state.active && this.state.status == "open") {
			this.toggle("closed");
		}

		this.setAttribute("status", this.state.status);
		this.setAttribute("active", this.state.active ? "true" : "false");

		this.setLabelText();

		this.setAriaExpanded();

		document.documentElement.setAttribute("data-menu-active", this.state.active);
		document.documentElement.setAttribute("data-menu-status", this.state.status);

		document.documentElement.setAttribute("data-overlay", this.state.status);
	}
}

if ("customElements" in window) {
	customElements.define("toggle-menu", ToggleMenu);
}
